import React, { useEffect, useState } from 'react';
import { db, collection, getDocs, doc } from '../services/firebase';
import { deleteDoc, updateDoc } from 'firebase/firestore';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Legend, PieChart, Pie, Cell } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const blue = '#3757f7';
const blueLight = '#eaf0ff';
const green = '#27ae60';
const cardBg = '#fff';
const border = '#e0e0e0';
const pastelBg = '#f7f9fd';
const beanzColor = '#b71c1c';
const qlubColor = '#ff9800';
const talabatColor = '#a259ff';
const cardColorMap = [blue, green, beanzColor, qlubColor];

const graphTypes = [
  { key: 'line', label: 'Line Chart' },
  { key: 'bar', label: 'Bar Chart' },
  { key: 'pie', label: 'Pie Chart' },
];

// Admin-only page: View all users' weekly sales reports in a box-in-box layout. Read-only.

const AdminSalesReportPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGraphs, setActiveGraphs] = useState({}); // { [reportId]: 'line' }
  const [deletingId, setDeletingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editFields, setEditFields] = useState({ weekName: '', staffName: '', startDate: '', endDate: '', entries: [] });
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('wakra');

  useEffect(() => {
    const fetchReports = async () => {
      const snap = await getDocs(collection(db, 'weeklySalesReports'));
      const data = [];
      snap.forEach(doc => data.push({ id: doc.id, ...doc.data() }));
      // Sort by weekName (try to extract number if possible)
      data.sort((a, b) => {
        const getNum = (w) => {
          if (!w) return 0;
          const m = w.match(/(\d+)/);
          return m ? parseInt(m[1], 10) : 0;
        };
        return getNum(a.weekName) - getNum(b.weekName);
      });
      setReports(data);
      // Set default graph for each report
      const defaultGraphs = {};
      data.forEach(r => { defaultGraphs[r.id] = 'line'; });
      setActiveGraphs(defaultGraphs);
      setLoading(false);
    };
    fetchReports();
  }, []);

  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report? This cannot be undone.')) return;
    setDeletingId(reportId);
    try {
      await deleteDoc(doc(db, 'weeklySalesReports', reportId));
      setReports(reports => reports.filter(r => r.id !== reportId));
    } catch (err) {
      alert('Failed to delete report: ' + err.message);
    }
    setDeletingId(null);
  };

  const handleEdit = (report) => {
    setEditId(report.id);
    setEditFields({
      weekName: report.weekName || '',
      staffName: report.staffName || '',
      startDate: report.startDate && report.startDate.seconds ? new Date(report.startDate.seconds * 1000).toISOString().slice(0,10) : (report.startDate ? new Date(report.startDate).toISOString().slice(0,10) : ''),
      endDate: report.endDate && report.endDate.seconds ? new Date(report.endDate.seconds * 1000).toISOString().slice(0,10) : (report.endDate ? new Date(report.endDate).toISOString().slice(0,10) : ''),
      entries: report.entries || [],
    });
  };

  const handleEditField = (field, value) => {
    setEditFields(f => ({ ...f, [field]: value }));
  };

  const handleEditEntry = (index, field, value) => {
    setEditFields(f => {
      const entries = f.entries.map((row, i) => {
        if (i !== index) return row;
        const updated = { ...row, [field]: value };
        // Recalculate total for this row
        updated.total =
          (parseFloat(updated.cash) || 0) +
          (parseFloat(updated.card) || 0) +
          (parseFloat(updated.beanz) || 0) +
          (parseFloat(updated.qlub) || 0);
        return updated;
      });
      return { ...f, entries };
    });
  };

  const handleEditSave = async (report) => {
    try {
      await updateDoc(doc(db, 'weeklySalesReports', report.id), {
        weekName: editFields.weekName,
        staffName: editFields.staffName,
        startDate: new Date(editFields.startDate),
        endDate: new Date(editFields.endDate),
        entries: editFields.entries,
      });
      setReports(reports => reports.map(r => r.id === report.id ? {
        ...r,
        weekName: editFields.weekName,
        staffName: editFields.staffName,
        startDate: new Date(editFields.startDate),
        endDate: new Date(editFields.endDate),
        entries: editFields.entries,
      } : r));
      setEditId(null);
    } catch (err) {
      alert('Failed to save changes: ' + err.message);
    }
  };

  const handleEditCancel = () => {
    setEditId(null);
  };

  const handleSelect = (id) => {
    setSelectedIds(ids => ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id]);
  };

  const handleExportPDF = (exportReports) => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 32;
    const tableWidth = (pageWidth - margin * 3) / 2; // 2 tables per row
    let x = margin, y = margin;
    let col = 0;
    exportReports.forEach((report, idx) => {
      // Prepare table data
      const head = [[
        'DATE', 'CASH', 'CARD/SKIPCASH', 'BEANZ', 'QLUB', 'Talabat', 'TOTAL'
      ]];
      const body = (report.entries || []).map(row => [
        row.date && row.date.seconds ? new Date(row.date.seconds * 1000).toLocaleDateString() : (row.date ? new Date(row.date).toLocaleDateString() : ''),
        row.cash || '',
        row.card || '',
        row.beanz || '',
        row.qlub || '',
        row.talabat || '',
        parseFloat(row.total || 0).toFixed(2)
      ]);
      // Add summary row
      body.push(['', '', '', '', '', '',
        (report.entries || []).reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0).toFixed(2)
      ]);
      // Duration label
      const duration = `BRANCH: ${(report.branch ? report.branch.charAt(0).toUpperCase() + report.branch.slice(1) : 'Wakra')}\nDURATION: ${report.startDate && (report.startDate.seconds ? new Date(report.startDate.seconds * 1000).toLocaleDateString() : new Date(report.startDate).toLocaleDateString())} - ${report.endDate && (report.endDate.seconds ? new Date(report.endDate.seconds * 1000).toLocaleDateString() : new Date(report.endDate).toLocaleDateString())}`;
      // Table title
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('WEEKLY SALES REPORT', x + 2, y + 12);
      doc.setFont('helvetica', 'normal');
      doc.text(duration, x + 2, y + 36);
      // Table
      autoTable(doc, {
        startY: y + 52,
        startX: x,
        head,
        body,
        theme: 'grid',
        tableWidth,
        styles: { fontSize: 10, cellPadding: 4, halign: 'center', valign: 'middle' },
        headStyles: { fillColor: [40, 40, 40], textColor: 255, fontStyle: 'bold' },
        bodyStyles: { textColor: 30 },
        footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
        margin: { left: x, right: margin },
        didParseCell: function (data) {
          // Color columns
          if (data.section === 'body') {
            if (data.column.index === 1) data.cell.styles.textColor = [39, 174, 96]; // Cash: green
            if (data.column.index === 2) data.cell.styles.textColor = [55, 87, 247]; // Card: blue
            if (data.column.index === 3) data.cell.styles.textColor = [183, 28, 28]; // Beanz: red
            if (data.column.index === 4) data.cell.styles.textColor = [255, 152, 0]; // Qlub: orange
            if (data.column.index === 5) data.cell.styles.textColor = [155, 89, 182]; // Talabat: purple
          }
          // Bold summary row
          if (data.row.index === body.length - 1) {
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });
      // Move x/y for next table
      if (col === 0) {
        x += tableWidth + margin;
        col = 1;
      } else {
        x = margin;
        y += 220; // height per table
        col = 0;
        // Add new page if needed
        if (y + 220 > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          y = margin;
        }
      }
    });
    doc.save('WeeklySalesReports.pdf');
  };

  // Filter reports by branch
  const filteredReports = reports.filter(r => (r.branch || 'wakra') === selectedBranch);

  // Before rendering the list of reports, add a graph for weekly earnings for the selected branch
  const weeklyEarningsData = filteredReports.map(r => ({
    name: r.weekName || (r.startDate && r.endDate ? `${new Date(r.startDate.seconds ? r.startDate.seconds * 1000 : r.startDate).toLocaleDateString()} - ${new Date(r.endDate.seconds ? r.endDate.seconds * 1000 : r.endDate).toLocaleDateString()}` : 'Week'),
    total: (r.entries || []).reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0)
  }));

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ minHeight: '100vh', background: pastelBg, padding: 16 }}>
      <h1 style={{ fontWeight: 900, fontSize: 28, marginBottom: 24, color: blue, textAlign: 'center' }}>All Weekly Sales Reports</h1>
      {reports.length === 0 && <div>No reports found.</div>}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => setSelectedBranch('wakra')}
          style={{ background: selectedBranch === 'wakra' ? blue : blueLight, color: selectedBranch === 'wakra' ? '#fff' : blue, fontWeight: 900, borderRadius: 16, border: 'none', fontSize: 18, padding: '10px 28px', cursor: 'pointer', boxShadow: selectedBranch === 'wakra' ? '0 2px 12px #3757f733' : 'none', transition: 'background 0.18s' }}
        >
          Wakra
        </button>
        <button
          onClick={() => setSelectedBranch('gewan')}
          style={{ background: selectedBranch === 'gewan' ? blue : blueLight, color: selectedBranch === 'gewan' ? '#fff' : blue, fontWeight: 900, borderRadius: 16, border: 'none', fontSize: 18, padding: '10px 28px', cursor: 'pointer', boxShadow: selectedBranch === 'gewan' ? '0 2px 12px #3757f733' : 'none', transition: 'background 0.18s' }}
        >
          Gewan
        </button>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={weeklyEarningsData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={border} />
          <XAxis dataKey="name" fontSize={12} tick={{ fill: blue }} />
          <YAxis fontSize={12} tick={{ fill: blue }} width={60} />
          <Tooltip formatter={v => `QAR ${v}`} />
          <Line type="monotone" dataKey="total" stroke={green} strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
        </LineChart>
      </ResponsiveContainer>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 18 }}>
        <button
          onClick={() => handleExportPDF(filteredReports)}
          style={{ background: blue, color: '#fff', fontWeight: 800, borderRadius: 10, border: 'none', padding: '10px 18px', fontSize: 15, marginBottom: 0, cursor: 'pointer' }}
        >
          Export All as PDF
        </button>
        <button
          onClick={() => handleExportPDF(reports.filter(r => selectedIds.includes(r.id)))}
          disabled={selectedIds.length === 0}
          style={{ background: selectedIds.length ? blue : '#ccc', color: '#fff', fontWeight: 800, borderRadius: 10, border: 'none', padding: '10px 18px', fontSize: 15, marginBottom: 0, cursor: selectedIds.length ? 'pointer' : 'not-allowed' }}
        >
          Export Selected as PDF
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 32, maxWidth: 600, margin: '40px auto 0 auto', justifyContent: 'center' }}>
        {filteredReports.map((report, idx) => {
          const chartData = (report.entries || []).map(row => ({
            name: row.date && row.date.seconds ? new Date(row.date.seconds * 1000).toLocaleDateString() : row.date,
            Cash: parseFloat(row.cash) || 0,
            Card: parseFloat(row.card) || 0,
            Beanz: parseFloat(row.beanz) || 0,
            Qlub: parseFloat(row.qlub) || 0,
            Talabat: parseFloat(row.talabat) || 0,
            Total: parseFloat(row.total) || 0,
          }));
          const weeklyTotal = chartData.reduce((sum, d) => sum + d.Total, 0);
          const expanded = expandedId === report.id;
          return (
            <div key={report.id} style={{
              background: cardBg,
              borderRadius: 24,
              boxShadow: '0 2px 16px #e0e0e055',
              padding: 0,
              position: 'relative',
              overflow: 'hidden',
              transition: 'box-shadow 0.2s',
              minHeight: 120,
            }}>
              {/* Card summary (always visible) */}
              <div
                style={{
                  padding: '28px 24px 18px 24px',
                  cursor: 'pointer',
                  borderBottom: expanded ? `2px solid ${blue}` : 'none',
                  background: expanded ? blueLight : cardBg,
                  transition: 'background 0.2s',
                }}
                onClick={() => setExpandedId(expanded ? null : report.id)}
              >
                <div style={{ fontWeight: 900, fontSize: 22, color: blue, marginBottom: 6, textAlign: 'center' }}>{report.weekName || '-'}</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: blue, marginBottom: 4, textAlign: 'center' }}>
                  Branch: {report.branch ? (report.branch.charAt(0).toUpperCase() + report.branch.slice(1)) : 'Wakra'}
                </div>
                <div style={{ fontWeight: 800, fontSize: 18, color: green, marginBottom: 4, textAlign: 'center' }}>QAR {weeklyTotal.toFixed(2)}</div>
              </div>
              {/* Expandable details */}
              <div
                style={{
                  maxHeight: expanded ? 2000 : 0,
                  opacity: expanded ? 1 : 0,
                  overflow: 'hidden',
                  transition: 'max-height 0.5s cubic-bezier(0.4,0,0.2,1), opacity 0.3s',
                  background: cardBg,
                  padding: expanded ? 24 : '0 24px',
                  pointerEvents: expanded ? 'auto' : 'none',
                }}
              >
                {/* Edit and Delete buttons */}
                <button
                  onClick={() => handleEdit(report)}
                  title="Edit this report"
                  style={{ position: 'absolute', top: 18, right: 108, background: '#fff', border: `2px solid ${blue}`, color: blue, borderRadius: '50%', width: 36, height: 36, fontSize: 18, fontWeight: 900, cursor: 'pointer', boxShadow: '0 2px 8px #e0e0e022', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border 0.18s', marginRight: 8 }}
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDelete(report.id)}
                  disabled={deletingId === report.id}
                  title="Delete this report"
                  style={{ position: 'absolute', top: 18, right: 18, background: '#fff', border: `2px solid ${deletingId === report.id ? '#ccc' : '#e74c3c'}`, color: '#e74c3c', borderRadius: '50%', width: 36, height: 36, fontSize: 18, fontWeight: 900, cursor: deletingId === report.id ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px #e0e0e022', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border 0.18s' }}
                >
                  {deletingId === report.id ? '…' : '🗑️'}
                </button>
                {/* Export PDF button */}
                <div style={{ position: 'absolute', top: 18, left: 18 }}>
                  <input type="checkbox" checked={selectedIds.includes(report.id)} onChange={() => handleSelect(report.id)} />
                </div>
                <button
                  onClick={() => handleExportPDF([report])}
                  title="Export this week as PDF"
                  style={{ position: 'absolute', top: 18, right: 62, background: '#fff', border: `2px solid ${blue}`, color: blue, borderRadius: '50%', width: 36, height: 36, fontSize: 18, fontWeight: 900, cursor: 'pointer', boxShadow: '0 2px 8px #e0e0e022', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border 0.18s', marginRight: 8 }}
                >
                  📄
                </button>
                {/* Editable fields if in edit mode */}
                {editId === report.id ? (
                  <div style={{ margin: '18px 0', textAlign: 'center' }}>
                    <input
                      type="text"
                      value={editFields.weekName}
                      onChange={e => handleEditField('weekName', e.target.value)}
                      placeholder="Week Name"
                      style={{ fontSize: 18, padding: '10px 14px', borderRadius: 10, border: `2px solid ${blue}`, marginBottom: 10, width: 180, fontWeight: 700 }}
                    />
                    <input
                      type="text"
                      value={editFields.staffName}
                      onChange={e => handleEditField('staffName', e.target.value)}
                      placeholder="Staff Name"
                      style={{ fontSize: 18, padding: '10px 14px', borderRadius: 10, border: `2px solid ${blue}`, marginBottom: 10, width: 180, fontWeight: 700, marginLeft: 10 }}
                    />
                    <input
                      type="date"
                      value={editFields.startDate}
                      onChange={e => handleEditField('startDate', e.target.value)}
                      style={{ fontSize: 16, padding: '8px 10px', borderRadius: 8, border: `2px solid ${blue}`, marginBottom: 10, marginLeft: 10 }}
                    />
                    <input
                      type="date"
                      value={editFields.endDate}
                      onChange={e => handleEditField('endDate', e.target.value)}
                      style={{ fontSize: 16, padding: '8px 10px', borderRadius: 8, border: `2px solid ${blue}`, marginBottom: 10, marginLeft: 10 }}
                    />
                    {/* Editable sales table */}
                    <div style={{ overflowX: 'auto', marginTop: 18 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', background: cardBg, borderRadius: 18, boxShadow: '0 2px 12px #e0e0e033', overflow: 'hidden', minWidth: 600 }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Date</th>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Cash</th>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Card/SkipCash</th>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Beanz</th>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Qlub</th>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Talabat</th>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editFields.entries && editFields.entries.map((row, idx) => (
                            <tr key={idx}>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', fontWeight: 700 }}>{row.date && row.date.seconds ? new Date(row.date.seconds * 1000).toLocaleDateString() : (row.date ? new Date(row.date).toLocaleDateString() : '')}</td>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', color: green }}>
                                <input type="number" value={row.cash || ''} min="0" step="0.01" style={{ width: 70, fontSize: 15, borderRadius: 6, border: `1.5px solid ${green}` }} onChange={e => handleEditEntry(idx, 'cash', e.target.value)} />
                              </td>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', color: blue }}>
                                <input type="number" value={row.card || ''} min="0" step="0.01" style={{ width: 70, fontSize: 15, borderRadius: 6, border: `1.5px solid ${blue}` }} onChange={e => handleEditEntry(idx, 'card', e.target.value)} />
                              </td>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', color: beanzColor }}>
                                <input type="number" value={row.beanz || ''} min="0" step="0.01" style={{ width: 70, fontSize: 15, borderRadius: 6, border: `1.5px solid ${beanzColor}` }} onChange={e => handleEditEntry(idx, 'beanz', e.target.value)} />
                              </td>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', color: qlubColor }}>
                                <input type="number" value={row.qlub || ''} min="0" step="0.01" style={{ width: 70, fontSize: 15, borderRadius: 6, border: `1.5px solid ${qlubColor}` }} onChange={e => handleEditEntry(idx, 'qlub', e.target.value)} />
                              </td>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', color: talabatColor }}>
                                <input type="number" value={row.talabat || ''} min="0" step="0.01" style={{ width: 70, fontSize: 15, borderRadius: 6, border: `1.5px solid ${talabatColor}` }} onChange={e => handleEditEntry(idx, 'talabat', e.target.value)} />
                              </td>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', fontWeight: 700 }}>{parseFloat(row.total || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td style={{ fontWeight: 900, fontSize: 16, color: blue, padding: '10px 6px' }}>Total Weekly Sales</td>
                            <td colSpan={4} />
                            <td style={{ fontWeight: 900, fontSize: 16, color: green, padding: '10px 6px' }}>{editFields.entries ? editFields.entries.reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0).toFixed(2) : '0.00'}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <button onClick={() => handleEditSave(report)} style={{ fontSize: 16, fontWeight: 900, color: '#fff', background: green, border: 'none', borderRadius: 8, padding: '8px 18px', marginRight: 10, cursor: 'pointer' }}>Save</button>
                      <button onClick={handleEditCancel} style={{ fontSize: 16, fontWeight: 900, color: blue, background: '#fff', border: `2px solid ${blue}`, borderRadius: 8, padding: '8px 18px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontWeight: 900, fontSize: 20, color: blue, marginBottom: 6, textAlign: 'center', marginTop: 8 }}>
                      Entered by: <span style={{ color: '#222' }}>{report.staffName || report.userId || 'Unknown'}</span>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#888', marginBottom: 10, textAlign: 'center' }}>
                      Duration: {report.startDate && (report.startDate.seconds ? new Date(report.startDate.seconds * 1000).toLocaleDateString() : new Date(report.startDate).toLocaleDateString())} – {report.endDate && (report.endDate.seconds ? new Date(report.endDate.seconds * 1000).toLocaleDateString() : new Date(report.endDate).toLocaleDateString())}
                    </div>
                    {/* Graph Selector Buttons */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 18 }}>
                      {graphTypes.map(g => (
                        <button
                          key={g.key}
                          onClick={() => setActiveGraphs({ ...activeGraphs, [report.id]: g.key })}
                          style={{
                            fontSize: 16,
                            borderRadius: 12,
                            border: 'none',
                            background: (activeGraphs[report.id] || 'line') === g.key ? blue : blueLight,
                            color: (activeGraphs[report.id] || 'line') === g.key ? '#fff' : blue,
                            fontWeight: 800,
                            padding: '10px 18px',
                            cursor: 'pointer',
                            boxShadow: (activeGraphs[report.id] || 'line') === g.key ? '0 2px 8px #3757f733' : 'none',
                            transition: 'background 0.18s',
                          }}
                        >
                          {g.label}
                        </button>
                      ))}
                    </div>
                    {/* Graphs */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginBottom: 24 }}>
                      {(activeGraphs[report.id] || 'line') === 'line' && (
                        <div style={{ background: blueLight, borderRadius: 18, padding: 16, boxShadow: '0 2px 8px #eaf0ff55' }}>
                          <div style={{ fontWeight: 800, fontSize: 16, color: blue, marginBottom: 8 }}>Daily Total Sales</div>
                          <ResponsiveContainer width="100%" height={180}>
                            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke={border} />
                              <XAxis dataKey="name" fontSize={12} tick={{ fill: blue }} />
                              <YAxis fontSize={12} tick={{ fill: blue }} width={40} />
                              <Tooltip formatter={v => `QAR ${v}`} />
                              <Line type="monotone" dataKey="Total" stroke={green} strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                      {(activeGraphs[report.id] || 'line') === 'bar' && (
                        <div style={{ background: blueLight, borderRadius: 18, padding: 16, boxShadow: '0 2px 8px #eaf0ff55' }}>
                          <div style={{ fontWeight: 800, fontSize: 16, color: blue, marginBottom: 8 }}>Payment Types per Day</div>
                          <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke={border} />
                              <XAxis dataKey="name" fontSize={12} tick={{ fill: blue }} />
                              <YAxis fontSize={12} tick={{ fill: blue }} width={40} />
                              <Tooltip formatter={v => `QAR ${v}`} />
                              <Legend />
                              <Bar dataKey="Cash" stackId="a" fill={green} />
                              <Bar dataKey="Card" stackId="a" fill={blue} />
                              <Bar dataKey="Beanz" stackId="a" fill={beanzColor} />
                              <Bar dataKey="Qlub" stackId="a" fill={qlubColor} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                      {(activeGraphs[report.id] || 'line') === 'pie' && (
                        <div style={{ background: blueLight, borderRadius: 18, padding: 16, boxShadow: '0 2px 8px #eaf0ff55', textAlign: 'center' }}>
                          <div style={{ fontWeight: 800, fontSize: 16, color: blue, marginBottom: 8 }}>Payment Type Proportion</div>
                          <ResponsiveContainer width="100%" height={180}>
                            <PieChart>
                              <Pie data={[
                                { name: 'Cash', value: chartData.reduce((sum, d) => sum + d.Cash, 0), color: green },
                                { name: 'Card', value: chartData.reduce((sum, d) => sum + d.Card, 0), color: blue },
                                { name: 'Beanz', value: chartData.reduce((sum, d) => sum + d.Beanz, 0), color: beanzColor },
                                { name: 'Qlub', value: chartData.reduce((sum, d) => sum + d.Qlub, 0), color: qlubColor },
                              ]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label>
                                {[
                                  { name: 'Cash', value: chartData.reduce((sum, d) => sum + d.Cash, 0), color: green },
                                  { name: 'Card', value: chartData.reduce((sum, d) => sum + d.Card, 0), color: blue },
                                  { name: 'Beanz', value: chartData.reduce((sum, d) => sum + d.Beanz, 0), color: beanzColor },
                                  { name: 'Qlub', value: chartData.reduce((sum, d) => sum + d.Qlub, 0), color: qlubColor },
                                ].map((entry, i) => (
                                  <Cell key={entry.name} fill={entry.color} />
                                ))}
                              </Pie>
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                    {/* Table */}
                    <div style={{ overflowX: 'auto', marginTop: 18 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', background: cardBg, borderRadius: 18, boxShadow: '0 2px 12px #e0e0e033', overflow: 'hidden', minWidth: 600 }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Date</th>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Cash</th>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Card/SkipCash</th>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Beanz</th>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Qlub</th>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Talabat</th>
                            <th style={{ padding: '12px 6px', fontWeight: 800, fontSize: 16, background: blueLight, color: blue }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(report.entries || []).map((row, idx) => (
                            <tr key={idx}>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', fontWeight: 700 }}>{row.date && row.date.seconds ? new Date(row.date.seconds * 1000).toLocaleDateString() : row.date}</td>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', color: green }}>{row.cash || 0}</td>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', color: blue }}>{row.card || 0}</td>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', color: beanzColor }}>{row.beanz || 0}</td>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', color: qlubColor }}>{row.qlub || 0}</td>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', color: talabatColor }}>{row.talabat || 0}</td>
                              <td style={{ borderBottom: `1px solid ${border}`, padding: '8px 6px', fontWeight: 700 }}>{parseFloat(row.total || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td style={{ fontWeight: 900, fontSize: 16, color: blue, padding: '10px 6px' }}>Total Weekly Sales</td>
                            <td colSpan={4} />
                            <td style={{ fontWeight: 900, fontSize: 16, color: green, padding: '10px 6px' }}>{weeklyTotal.toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 40 }}>
        <button
          onClick={() => window.location.href = '/admin'}
          style={{ width: '100%', maxWidth: 400, margin: '0 auto 18px auto', display: 'block', fontSize: 18, borderRadius: 20, border: 'none', background: blue, color: '#fff', fontWeight: 900, letterSpacing: 1, cursor: 'pointer', boxShadow: '0 2px 12px #3757f711', padding: '16px 0', transition: 'background 0.18s' }}
        >
          Back to Admin
        </button>
        <button
          onClick={() => window.location.href = '/weekly-sales-report'}
          style={{ width: '100%', maxWidth: 400, margin: '0 auto 24px auto', display: 'block', fontSize: 18, borderRadius: 20, border: 'none', background: blue, color: '#fff', fontWeight: 900, letterSpacing: 1, cursor: 'pointer', boxShadow: '0 2px 12px #3757f711', padding: '16px 0', transition: 'background 0.18s' }}
        >
          Back to Sales Input
        </button>
      </div>
    </div>
  );
};

export default AdminSalesReportPage; 